import React from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import useAppNavigate from "hooks/useAppNavigate";
import { zoneAbbreviation } from "utils/dateUtils";
import RunStatusChip from "./RunStatusChip";
import RunTimestamp from "./RunTimestamp";
import TypeChipList from "./TypeChipList";
import type {
  IntegrationMonitorItem,
  IntegrationMonitorSortField,
} from "features/integrationsBeta/integrationsBeta";
import { usePlatformEnumLabels } from "features/integrationsBeta/platformEnums";
import {
  frequencyItemsForZone,
  resolveDisplayZone,
  timeHeaderSuffix,
  type TimezoneDisplayMode,
} from "features/integrationsBeta/timezoneDisplay";

const noWrapCellStyle = {
  whiteSpace: "nowrap" as const,
  overflow: "hidden" as const,
  textOverflow: "ellipsis" as const,
};

interface Column {
  key: string;
  label: string;
  /** Fixed proportional width (table-layout: fixed); long content truncates. */
  width: string;
  sortable?: boolean;
  sortField?: IntegrationMonitorSortField;
}

const COLUMNS: Column[] = [
  {
    key: "client_name",
    label: "Client",
    width: "11%",
    sortable: true,
    sortField: "client",
  },
  {
    key: "name",
    label: "Integration",
    width: "27%",
    sortable: true,
    sortField: "name",
  },
  { key: "source_services", label: "Source", width: "5%" },
  { key: "target_services", label: "Target", width: "5%" },
  {
    key: "enabled",
    label: "Enabled",
    width: "5%",
    sortable: true,
    sortField: "enabled",
  },
  { key: "frequency", label: "Frequency", width: "15%" },
  {
    key: "last_run_started_at",
    label: "Last Run",
    width: "12%",
    sortable: true,
    sortField: "last_run_started_at",
  },
  {
    key: "next_run_at",
    label: "Next Run",
    width: "12%",
    sortable: true,
    sortField: "next_run_at",
  },
  {
    key: "last_run_status",
    label: "Last Status",
    width: "8%",
    sortable: true,
    sortField: "last_run_status",
  },
];

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <TableRow key={i}>
          {COLUMNS.map((c) => (
            <TableCell key={c.key}>
              <Skeleton variant="text" width="80%" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

type Props = {
  integrations: IntegrationMonitorItem[];
  isLoading: boolean;
  isFetching: boolean;
  error?: unknown;
  sortBy?: IntegrationMonitorSortField;
  sortOrder: "asc" | "desc";
  onSortChange: (field: IntegrationMonitorSortField) => void;
  timezoneMode: TimezoneDisplayMode;
  pagination?: React.ReactNode;
};

const TIME_COLUMNS = new Set([
  "frequency",
  "last_run_started_at",
  "next_run_at",
]);

export default function IntegrationsBetaTable({
  integrations,
  isLoading,
  isFetching,
  error,
  sortBy,
  sortOrder,
  onSortChange,
  timezoneMode,
  pagination,
}: Props) {
  const navigate = useAppNavigate();
  const { statusLabel } = usePlatformEnumLabels();

  // Column-header zone hint: fixed for local/utc, per-row (grey abbreviation
  // in the cells) for client mode.
  const timeSuffix = timeHeaderSuffix(timezoneMode);
  const columnLabel = (col: Column) =>
    TIME_COLUMNS.has(col.key) && timeSuffix
      ? `${col.label} · ${timeSuffix}`
      : col.label;

  if (error) {
    return (
      <Typography p={2} variant="h6" align="center" color="error">
        Error loading integrations
      </Typography>
    );
  }

  const onRowClick = (id: number) => navigate(`/integrations-beta/${id}`);

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "clip",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "auto" }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            minWidth: 1440,
            width: "100%",
            tableLayout: "fixed",
            borderColor: "divider",
            // Default MUI cell padding (16px) - same left inset as the
            // User Management and Integration Details History tables.
          }}
        >
          <colgroup>
            {COLUMNS.map((col) => (
              <col key={col.key} style={{ width: col.width }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                >
                  {col.sortable && col.sortField ? (
                    <TableSortLabel
                      active={sortBy === col.sortField}
                      direction={sortBy === col.sortField ? sortOrder : "asc"}
                      onClick={() => onSortChange(col.sortField!)}
                    >
                      {columnLabel(col)}
                    </TableSortLabel>
                  ) : (
                    columnLabel(col)
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <SkeletonRows />
            ) : integrations.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS.length}
                  align="center"
                  sx={{ py: 6 }}
                >
                  <Typography color="text.secondary">
                    No integrations found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              integrations.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={() => onRowClick(row.id)}
                  sx={{
                    "&:last-child td, &:last-child th": {
                      borderBottom: "none",
                    },
                    opacity: isFetching ? 0.6 : 1,
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "calibrate.primarySoft06",
                    },
                  }}
                >
                  <TableCell sx={noWrapCellStyle} title={row.client_name}>
                    {row.client_name}
                  </TableCell>
                  <TableCell sx={noWrapCellStyle} title={row.name}>
                    {/* The integration's own zone rides as a superscript
                        next to the name; it never gets truncated away. */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ minWidth: 0, overflow: "hidden" }}
                      >
                        {row.name}
                      </Typography>
                      <Typography
                        component="sup"
                        sx={{
                          flexShrink: 0,
                          ml: 0.25,
                          fontSize: 10,
                          lineHeight: 1.2,
                          color: "text.secondary",
                        }}
                      >
                        {zoneAbbreviation(
                          new Date().toISOString(),
                          row.timezone,
                        )}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <TypeChipList items={row.source_services} />
                  </TableCell>
                  <TableCell>
                    <TypeChipList items={row.target_services} />
                  </TableCell>
                  <TableCell>
                    {row.enabled ? (
                      <CheckIcon
                        sx={{ color: "secondary.main", fontSize: 20 }}
                      />
                    ) : (
                      <CloseIcon
                        sx={{ color: "text.disabled", fontSize: 20 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <TypeChipList
                      items={
                        frequencyItemsForZone(
                          row.frequency,
                          row.active_schedule_crons,
                          row.timezone,
                          timezoneMode,
                        ).items
                      }
                    />
                  </TableCell>
                  <TableCell sx={noWrapCellStyle}>
                    {row.last_run_started_at ? (
                      <RunTimestamp
                        iso={row.last_run_started_at}
                        timeZone={resolveDisplayZone(
                          timezoneMode,
                          row.timezone,
                        )}
                        short
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell sx={noWrapCellStyle}>
                    {row.next_run_at ? (
                      <RunTimestamp
                        iso={row.next_run_at}
                        timeZone={resolveDisplayZone(
                          timezoneMode,
                          row.timezone,
                        )}
                        short
                        color="text.secondary"
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <RunStatusChip
                      status={row.last_run_status}
                      label={statusLabel(row.last_run_status)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
      {pagination}
    </Paper>
  );
}
